import { useEffect, useRef } from 'react';

const THROTTLE_MS = 150;
const VAR_NAME = '--app-height';

/**
 * Obtiene la altura visible del viewport de forma fiable.
 * En iOS Safari, window.innerHeight cambia al mostrar/ocultar la barra de direcciones;
 * visualViewport.height es la altura realmente visible.
 */
function getViewportHeight() {
  if (typeof window === 'undefined') return null;
  try {
    const vv = window.visualViewport;
    if (vv && typeof vv.height === 'number' && !Number.isNaN(vv.height)) {
      return vv.height;
    }
    const h = window.innerHeight;
    if (typeof h === 'number' && !Number.isNaN(h)) return h;
  } catch (_) {}
  return null;
}

/**
 * Hook que mantiene la variable CSS --app-height con la altura visible del viewport.
 * Soluciona el problema de 100vh en iOS Safari (barra de direcciones / home indicator).
 *
 * Uso:
 * 1. Llamar una vez en el árbol (p.ej. en App o en el layout raíz).
 * 2. En CSS/Tailwind usar: min-h-[var(--app-height)] o la clase .min-h-screen-safe.
 *
 * - Usa visualViewport cuando existe (iOS/Safari), sino innerHeight.
 * - Throttled para evitar reflows y jank.
 * - Limpia listeners al desmontar (no memory leaks).
 * - Compatible con Android Chrome y navegadores antiguos.
 */
export function useViewportHeight() {
  const rafRef = useRef(null);
  const lastSetRef = useRef(0);

  useEffect(() => {
    let isMounted = true;

    const setHeight = () => {
      if (!isMounted || typeof document === 'undefined') return;
      const height = getViewportHeight();
      if (height !== null && document.documentElement) {
        document.documentElement.style.setProperty(VAR_NAME, `${height}px`);
        lastSetRef.current = Date.now();
      }
    };

    const throttledSetHeight = () => {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const now = Date.now();
        if (now - lastSetRef.current >= THROTTLE_MS) {
          setHeight();
        }
      });
    };

    // Set initial value as soon as possible
    setHeight();

    const resizeHandler = () => throttledSetHeight();
    const vvResizeHandler = () => throttledSetHeight();
    const vvScrollHandler = () => throttledSetHeight();

    window.addEventListener('resize', resizeHandler, { passive: true });
    window.addEventListener('orientationchange', resizeHandler, { passive: true });

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', vvResizeHandler, { passive: true });
      vv.addEventListener('scroll', vvScrollHandler, { passive: true });
    }

    return () => {
      isMounted = false;
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      window.removeEventListener('resize', resizeHandler);
      window.removeEventListener('orientationchange', resizeHandler);
      if (vv) {
        vv.removeEventListener('resize', vvResizeHandler);
        vv.removeEventListener('scroll', vvScrollHandler);
      }
    };
  }, []);

  return { cssVar: VAR_NAME };
}

export default useViewportHeight;
